<script setup lang="ts">
import { useParentElement } from "@vueuse/core";
import type { SerializedEditorState } from "lexical";
import { computed, type HTMLAttributes, nextTick, ref, watch } from "vue";

import actions from "~/actions";
import {
  getPropertyDisplayTitle,
  getPropertyPartialTask,
  getPropertyValueFromTask,
  getShownPropertyWithConfigList,
} from "~/common/properties";
import Animated from "~/components/dumb/Animated.vue";
import AvatarGroup from "~/components/dumb/AvatarGroup.vue";
import DartboardChip from "~/components/dumb/DartboardChip.vue";
import LoadingBlock from "~/components/dumb/LoadingBlock.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import TitleEditor from "~/components/text/TitleEditor.vue";
import { convertToTask } from "~/components/text/utils";
import TaskChips from "~/components/visualization/components/TaskChips.vue";
import { ChevronDownIcon, ChildRelationshipIcon } from "~/icons";
import {
  DartboardKind,
  EditorMode,
  PageKind,
  Placement,
  PropertyKind,
  RelationshipKindKind,
  SubtaskDisplayMode,
} from "~/shared/enums";
import type { Property, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { getCharacterPosition, makeLexicalStateWithText, someInHierarchy } from "~/utils/common";
import { useAsyncTask, useAsyncTasksOrdered } from "~/utils/entityIncrementalLoading";
import { filePreviews } from "~/utils/fileUpload";
import { isImageType } from "~/utils/media";

const props = defineProps<{
  task: Task;
  hasShownParent?: boolean;
  includeDartboard?: boolean;
  noninteractive?: boolean;
  excludeProperties?: Property[];
  showCoverImage?: boolean;
  class?: HTMLAttributes["class"];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const parent = useParentElement();

const showAssigneeLabel = computed(
  () => props.task.assigneeDuids.length === 1 && dataStore.defaultAssigneesProperty.adtl.isNameShown
);

const excludedPropertyKinds = computed(
  () =>
    new Set([
      ...(showAssigneeLabel.value ? [] : [PropertyKind.DEFAULT_ASSIGNEES]),
      ...(dataStore.defaultStatusProperty.adtl.isNameShown ? [] : [PropertyKind.DEFAULT_STATUS]),
    ])
);

const shownProperties = computed(() => getShownPropertyWithConfigList());

const properties = computed(() =>
  shownProperties.value
    .filter(
      ([property]) =>
        !excludedPropertyKinds.value.has(property.kind) &&
        !props.excludeProperties?.some((p) => p.duid === property.duid)
    )
    .map(([property, config]) => {
      const chip = config.chip();
      return {
        duid: property.duid,
        title: getPropertyDisplayTitle(property),
        component: chip.component,
        values: chip.getValues(property, props.task),
      };
    })
);

const showStatus = computed(
  () =>
    shownProperties.value.some(([p]) => p.kind === PropertyKind.DEFAULT_STATUS) &&
    !props.excludeProperties?.some((p) => p.kind === PropertyKind.DEFAULT_STATUS)
);

const showAssignees = computed(
  () => tenantStore.assigneeEnabled && !props.excludeProperties?.some((p) => p.kind === PropertyKind.DEFAULT_ASSIGNEES)
);
const statusTooltipText = computed(() => dataStore.getStatusByDuid(props.task.statusDuid)?.title);

const titleEditor = ref<InstanceType<typeof TitleEditor> | null>(null);
const titleDiv = ref<HTMLDivElement | null>(null);
const titleState = ref<SerializedEditorState>(makeLexicalStateWithText(props.task.title));
const isEditingTitle = ref(false);
const { task: globalTask } = useAsyncTask(computed(() => props.task.duid));

const coverImageLoadFailed = ref(false);
const loadedCoverImageAttachment = computed(
  () =>
    dataStore.getAttachmentsByDuidsOrderedIfLoaded(props.task.attachmentDuids ?? []).find((e) => isImageType(e.kind)) ??
    null
);
const { state: coverImageAttachment, isLoading: isLoadingCoverImage } = asyncState(
  () => {
    coverImageLoadFailed.value = false;
    if (!props.showCoverImage || props.task.attachmentDuids.length === 0) {
      return Promise.resolve(null);
    }

    if (loadedCoverImageAttachment.value) {
      return Promise.resolve(loadedCoverImageAttachment.value);
    }

    return dataStore.getCoverImageAttachmentByTaskDuid(props.task.duid);
  },
  {
    default: loadedCoverImageAttachment.value,
    watch: [
      () => props.showCoverImage,
      () => props.task.duid,
      () => props.task.attachmentDuids.join(","),
      loadedCoverImageAttachment,
    ],
  }
);
const showCoverImageLoading = computed(
  () => !!props.showCoverImage && props.task.attachmentDuids.length > 0 && isLoadingCoverImage.value
);
const imageSrc = computed(() => {
  const att = coverImageAttachment.value;
  if (!att) {
    return "";
  }
  const preview = filePreviews.get(att.duid);
  return preview ?? att.fileUrl;
});

const handleImageError = () => {
  coverImageLoadFailed.value = true;
};

const canAddTasks = computed(
  () => appStore.currentPage?.pageKind !== PageKind.VIEW && appStore.currentPage?.kind !== DartboardKind.FINISHED
);

const openTaskInDetail = () => {
  if (isEditingTitle.value) {
    return;
  }

  appStore.openTaskDetailForTask(props.task.duid);
  actions.visualization.selectRowByIdAndScroll(props.task.duid);
};

const jumpToDescription = () => {
  actions.visualization.selectRowByIdAndScroll(props.task.duid);
  actions.visualization.jumpToDescription(props.task);
};

const jumpToComments = async () => {
  actions.visualization.selectRowByIdAndScroll(props.task.duid);
  await actions.visualization.jumpToComments(props.task);
};

const absentee = computed(() => "absentee" in props.task && props.task.absentee === true);
const notFlat = computed(() => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT);

const subtaskDuids = computed(() =>
  dataStore.getRelationshipsByKindKind(props.task, RelationshipKindKind.PARENT_OF, true).map((e) => e.targetDuid)
);

const { tasks: subtasks } = useAsyncTasksOrdered(
  computed(() => subtaskDuids.value),
  computed(() => ({ includeTrashed: false, includeDraft: false }))
);

const hasSubtasks = computed(() => subtasks.value.length > 0);
const numSubtasksCompleted = computed(
  () => subtasks.value.filter((task) => dataStore.defaultCompletedStatusDuids.has(task.statusDuid)).length
);

const showAgentWorking = computed(() => dataStore.isTaskAgentWorking(props.task));

// Update title if it changes elsewhere
watch(
  () => props.task.title,
  (newTitle) => {
    if (newTitle === undefined || titleEditor.value?.hasFocus) {
      return;
    }

    const editorState = makeLexicalStateWithText(newTitle ?? "");
    if (JSON.stringify(titleState.value) === JSON.stringify(editorState)) {
      return;
    }

    titleState.value = editorState;
    titleEditor.value?.setEditorState(titleState.value);
  }
);

// Update local title and globally
const updateTitle = (newTitle: SerializedEditorState) => {
  titleState.value = newTitle;

  if (!globalTask.value) {
    return;
  }
  globalTask.value.title = convertToTask(titleState.value).title ?? "";
};

const toggleExpanded = () => {
  if (pageStore.isPublicView) {
    return;
  }
  if (!notFlat.value) {
    openTaskInDetail();
    return;
  }
  dataStore.updateTasks([{ duid: props.task.duid, expanded: !props.task.expanded }]);
};

const startEditingTitle = (event?: MouseEvent) => {
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => {
    actions.visualization.selectRowByIdAndScroll(props.task.duid);
  });

  const position = getCharacterPosition(titleDiv.value, event);
  isEditingTitle.value = true;
  nextTick(() => {
    const editor = titleEditor.value;
    if (!editor) {
      return;
    }
    editor.focus();
    if (position === undefined) {
      return;
    }
    editor.select(position);
  });
};

const saveIfNotEmpty = async (event?: FocusEvent) => {
  isEditingTitle.value = false;
  const partialTask = convertToTask(titleState.value);
  const titleText = partialTask.title ?? "";
  if (
    titleText === "" &&
    !(event && someInHierarchy(event.relatedTarget as HTMLElement | null, (e) => e === parent.value))
  ) {
    dataStore.deleteTasks([props.task]);
    return false;
  }
  titleEditor.value?.setEditorState(makeLexicalStateWithText(titleText));

  let deleted = false;
  await dataStore.updateTaskFromNlp(props.task, partialTask, {
    onRemapFailed: () => {
      dataStore.deleteTasks([props.task]);
      deleted = true;
    },
  });
  return !deleted;
};

const onEnter = async (event: KeyboardEvent) => {
  event.stopPropagation();
  const created = await saveIfNotEmpty();
  if (!created || !canAddTasks.value) {
    return;
  }
  let partial = {};
  if (props.excludeProperties) {
    props.excludeProperties.forEach((property) => {
      const newPartial = getPropertyPartialTask(property, partial, getPropertyValueFromTask(property, props.task));
      partial = { ...partial, ...newPartial };
    });
  }
  const newTask = await actions.visualization.createTaskUnderneath(partial, { preventStartEditing: true });
  if (!newTask) {
    return;
  }
  appStore.board?.startEditingTask(newTask.duid);
};

const onTab = async (event: KeyboardEvent) => {
  event.preventDefault();
  await saveIfNotEmpty();
  if (event.shiftKey) {
    await actions.visualization.subtaskOutdent();
  } else {
    await actions.visualization.subtaskIndent();
  }
};

const onPaste = async (text: string) => {
  const visualization = appStore.getActiveVisualization();
  const allRows = visualization.getAll();
  const indexOfTask = allRows.findIndex((row) => row.duid === props.task.duid);
  const belowTaskOrder = (await dataStore.getTaskByDuid(allRows[indexOfTask + 1]?.duid))?.order ?? "";

  const parentTaskDuid = dataStore.getParentTaskIfLoaded(props.task)?.duid;

  const remainingText = actions.task.createTasksFromText(
    text,
    props.task.dartboardDuid,
    props.task.order,
    belowTaskOrder,
    { statusDuid: props.task.statusDuid },
    { parentTaskDuid }
  );
  titleState.value = makeLexicalStateWithText(remainingText ?? "");
  titleEditor.value?.setEditorState(titleState.value);
};

defineExpose({
  isEditingTitle,
  startEditingTitle,
});
</script>

<template>
  <div class="relative w-full overflow-hidden !leading-5" :class="[props.class, absentee && 'opacity-50']">
    <LoadingBlock v-if="showCoverImageLoading" loading class="mb-2 h-[128px] rounded-sm" />
    <div
      v-else-if="showCoverImage && coverImageAttachment && !coverImageLoadFailed"
      class="mb-2 flex max-h-[128px] w-full items-center justify-center overflow-hidden rounded-sm">
      <img :src="imageSrc" :alt="coverImageAttachment.name" class="w-full object-cover" @error="handleImageError" />
    </div>

    <div class="float-right mb-2 flex flex-col gap-2" :class="(showStatus || showAssignees || absentee) && 'ml-2'">
      <template v-if="!absentee">
        <Tooltip v-if="showStatus" :text="dataStore.defaultStatusProperty.title">
          <Tooltip :text="statusTooltipText" :placement="Placement.RIGHT">
            <StatusIcon :duid="task.statusDuid" class="icon-lg focus-ring-none" />
          </Tooltip>
        </Tooltip>
        <Tooltip v-if="showAssignees" :text="dataStore.defaultAssigneesProperty.title">
          <AvatarGroup
            :show-agent-working="showAgentWorking"
            :duids="task.assigneeDuids"
            :editor-mode="EditorMode.BOARD"
            class="!w-6"
            :limit="3"
            :first-user-only="!tenantStore.multipleAssigneesEnabled"
            vertical
            large
            :class="absentee && 'opacity-50'" />
        </Tooltip>
      </template>

      <Tooltip v-else class="dart-subtask-expand" :text="`${task.expanded ? 'Hide' : 'Show'} subtasks`">
        <button
          type="button"
          class="flex h-5 items-center justify-center gap-0.5 rounded-sm border border-oncolor px-0.5 text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
          tabindex="-1"
          @click.stop="toggleExpanded"
          @keydown.enter.stop="toggleExpanded">
          <span class="sr-only">Toggle subtasks</span>
          <ChevronDownIcon
            v-if="!noninteractive"
            class="icon-xs transition-transform"
            :class="!task.expanded && '-rotate-90'" />
          <ChildRelationshipIcon class="icon-xs" />
          <span class="text-xs">{{ `${numSubtasksCompleted}/${subtasks.length}` }}</span>
        </button>
      </Tooltip>
    </div>
    <div :class="[absentee && 'relative overflow-hidden', (absentee || !isEditingTitle) && 'mb-1.5 min-h-[22px]']">
      <div
        v-if="!isEditingTitle || absentee"
        ref="titleDiv"
        class="min-h-[22px] w-auto max-w-fit break-words !min-w-[40px] cursor-text overflow-hidden rounded-sm border border-transparent text-sm hyphens-auto whitespace-pre-wrap text-md hover:border-oncolor"
        :class="absentee && isEditingTitle && 'invisible'"
        @click.stop="startEditingTitle"
        @keydown.enter="() => startEditingTitle()">
        {{ task.title }}
      </div>
      <div v-if="isEditingTitle" class="overflow-hidden" :class="absentee && 'absolute inset-0'">
        <TitleEditor
          ref="titleEditor"
          :initial-title="titleState"
          :editor-mode="EditorMode.BOARD"
          content-classes="w-full"
          @paste-multiline-text="onPaste"
          @change="updateTitle"
          @blur="saveIfNotEmpty"
          @tab="onTab"
          @enter="onEnter" />
      </div>
    </div>
    <Animated
      v-if="!absentee"
      class="mb-1 flex flex-wrap items-center gap-1 empty:mb-0"
      :class="isEditingTitle ? 'mt-[3px]' : 'mt-0.5'">
      <Tooltip
        v-if="notFlat && hasSubtasks"
        class="dart-subtask-expand mb-1 inline-flex align-middle"
        :text="`${task.expanded ? 'Hide' : 'Show'} subtasks`">
        <button
          type="button"
          class="flex h-5 items-center justify-center gap-1 rounded-sm border border-oncolor pr-1.5 pl-1 text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
          tabindex="-1"
          @click.stop="toggleExpanded"
          @keydown.enter.stop="toggleExpanded">
          <span class="sr-only">Toggle subtasks</span>
          <ChevronDownIcon
            v-if="!noninteractive"
            class="icon-xs transition-transform"
            :class="!task.expanded && '-rotate-90'" />
          <ChildRelationshipIcon class="icon-xs" />
          <span class="text-xs">{{ `${numSubtasksCompleted}/${subtasks.length}` }}</span>
        </button>
      </Tooltip>
      <DartboardChip
        v-if="includeDartboard && !pageStore.isPublicView"
        class="mb-1 inline-flex align-middle"
        :selected-dartboard-duid="task.dartboardDuid"
        :editor-mode="EditorMode.BOARD" />
      <template v-if="!pageStore.isMobile">
        <component
          :is="property.component"
          v-for="property in properties"
          :key="property.duid"
          class="mb-1 inline-flex align-middle"
          v-bind="property.values"
          :editor-mode="EditorMode.BOARD" />
        <TaskChips
          :task="task"
          :editor-mode="EditorMode.BOARD"
          :has-shown-parent="Boolean(hasShownParent)"
          :subtasks-expanded="task.expanded"
          :hide-subtasks="notFlat && hasSubtasks"
          custom-styles
          @open-task-in-detail="openTaskInDetail"
          @jump-to-description="jumpToDescription"
          @jump-to-comments="jumpToComments" />
      </template>
    </Animated>
  </div>
</template>
